import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const MessageContext = createContext();

export const useMessages = () => {
  return useContext(MessageContext);
};

export const MessageProvider = ({ children }) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) {
      setConversations([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    api.get('/messaging/threads/')
      .then(res => {
        setConversations(Array.isArray(res.data.results) ? res.data.results : []);
        setLoading(false);
      })
      .catch(err => {
        setError('Failed to load conversations.');
        setLoading(false);
      });
  }, [currentUser]);

  const getConversationById = (id) => {
    return conversations.find(c => c.id === id);
  };

  // createConversation for starting a new thread
  const createConversation = async (sellerId, message, productId) => {
    if (!currentUser) return null;
    try {
      const payload = {
        buyer: currentUser.id,
        seller: sellerId
      };
      if (productId) payload.product = productId;
      const res = await api.post('/messaging/threads/', payload);
      const thread = res.data;
      // Send the first message
      await api.post('/messaging/messages/', {
        thread: thread.id,
        content: message
      });
      return thread.id;
    } catch (error) {
      console.error('Error creating conversation:', error);
      throw error;
    }
  };

  const fetchThreadById = async (threadId) => {
    try {
      const res = await api.get(`/messaging/threads/${threadId}/`);
      return res.data;
    } catch (error) {
      console.error('Error fetching thread:', error);
      return null;
    }
  };

  // sendMessage for replying in an existing thread
  const sendMessage = async (threadId, text) => {
    await api.post('/messaging/messages/', {
      thread: threadId,
      content: text
    });
    // Refetch the thread after sending
    return await fetchThreadById(threadId);
  };

  const markAsRead = async (threadId) => {
    try {
      await api.post(`/messaging/threads/${threadId}/mark_read/`);
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteConversation = async (threadId) => {
    try {
      await api.delete(`/messaging/threads/${threadId}/`);
      // Remove from local state
      setConversations(prev => prev.filter(conv => conv.id !== threadId));
    } catch (error) {
      console.error('Error deleting conversation:', error);
      throw error;
    }
  };

  const value = {
    conversations,
    loading,
    error,
    getConversationById,
    createConversation,
    sendMessage,
    markAsRead,
    fetchThreadById,
    deleteConversation
  };

  return (
    <MessageContext.Provider value={value}>
      {children}
    </MessageContext.Provider>
  );
};

export default MessageProvider;